import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * TVG-359: whether this environment offers the QA fixture tooling, and whether
 * the contact on screen is one of the seeded fixtures.
 *
 * The server answers 404 wherever App\Support\QaFixtures\QaFixtureGuard says
 * no — which is every host except pre-production — so a caller that treats any
 * error as "not available" cannot show a QA control on production.
 */
export interface QaFixtureStatus {
  available: boolean;
  is_fixture_contact: boolean;
  mail_catcher: string;
}

export interface QaQuoteEmailResult {
  quote_id: number;
  venue: string;
  sent_to: string;
  email: string;
  mail_catcher: string;
}

/** TVG-379: the customer emails QA tools can send afresh, as the API names them. */
export type QaCustomerEmailKey =
  | 'first_contact'
  | 'follow_up_template'
  | 'enquiry_follow_up_1'
  | 'enquiry_follow_up_2'
  | 'post_viewing';

export interface QaCustomerEmailResult {
  email: string;
  sent_to: string;
  venue: string | null;
  mail_catcher: string;
}

@Injectable({
  providedIn: 'root',
})
export class QaFixtureService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  status(contactEmail: string): Observable<{ data: QaFixtureStatus }> {
    const params = new HttpParams().set('contact_email', contactEmail);

    return this.http.get<{ data: QaFixtureStatus }>(`${this.apiUrl}/api/qa/fixtures/status`, {
      params,
    });
  }

  sendQuoteEmail(contactEmail: string): Observable<{ data: QaQuoteEmailResult }> {
    return this.http.post<{ data: QaQuoteEmailResult }>(
      `${this.apiUrl}/api/qa/fixtures/quote-email`,
      { contact_email: contactEmail },
    );
  }

  sendCustomerEmail(contactEmail: string, email: QaCustomerEmailKey): Observable<{ data: QaCustomerEmailResult }> {
    return this.http.post<{ data: QaCustomerEmailResult }>(`${this.apiUrl}/api/qa/fixtures/customer-email`, {
      contact_email: contactEmail,
      email,
    });
  }
}
